import React, { useState } from 'react';
import { getMexicoDateKey, formatMexicoDate } from '../../dateUtils';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users } from 'lucide-react';

const TicketsTrend = ({ tickets, cardClasses, isDarkMode, dateFilter}) => {
  // Estado para controlar qué series están visibles
  const [visibleSeries, setVisibleSeries] = useState({
    Critica: true,
    Alta: true,
    Media: true,
    Baja: true
  });

  // Toggle de visibilidad de una serie
  const toggleSeries = (seriesName) => {
    setVisibleSeries(prev => ({
      ...prev,
      [seriesName]: !prev[seriesName]
    }));
  };
  // Agrupar tickets por fecha y prioridad
  const ticketTrendData = tickets.reduce((acc, ticket) => {
    if (!ticket.Hora_de_creacion) return acc;
    
    const fechaCompleta = new Date(ticket.Hora_de_creacion);
    const fecha = getMexicoDateKey(fechaCompleta);
    const prioridad = ticket.Prioridad;
    
    const existingDate = acc.find(item => item.date === fecha);
    if (existingDate) {
      existingDate[prioridad] = (existingDate[prioridad] || 0) + 1;
    } else {
      acc.push({ 
        date: fecha,
        formatted: formatMexicoDate(fechaCompleta),
        Critica: prioridad === 'Critica' ? 1 : 0,
        Alta: prioridad === 'Alta' ? 1 : 0,
        Media: prioridad === 'Media' ? 1 : 0,
        Baja: prioridad === 'Baja' ? 1 : 0
      });
    }
    return acc;
  }, []).sort((a, b) => new Date(a.date) - new Date(b.date));

  // Calcular el máximo valor para optimizar el eje Y
  const maxValue = Math.max(
    ...ticketTrendData.map(d => Math.max(d.Alta || 0, d.Media || 0, d.Baja || 0))
  );
  const yAxisMax = Math.max(Math.ceil(maxValue * 1.2), 1); // Mínimo 1 para evitar errores

  return (
    <div className={`${cardClasses} p-6 rounded-2xl mb-8`}>
      <h3 className="text-xl font-bold mb-4 flex items-center">
        <Users className="w-6 h-6 mr-2 text-blue-400" />
        Tendencia de Tickets por Prioridad ({dateFilter})
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={ticketTrendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <defs>
            
            {/* Gradiente para Critica Prioridad */}
            <linearGradient id="criticaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#DC2626" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#DC2626" stopOpacity={0.1}/>
            </linearGradient>

            {/* Gradiente para Alta Prioridad */}
            <linearGradient id="altaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#EA580C" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#EA580C" stopOpacity={0.1}/>
            </linearGradient>
            
            {/* Gradiente para Media Prioridad */}
            <linearGradient id="mediaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.1}/>
            </linearGradient>
            
            {/* Gradiente para Baja Prioridad */}
            <linearGradient id="bajaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
          <XAxis 
            dataKey="formatted" 
            stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
            fontSize={11}
            axisLine={false}
            tickLine={false}
          />
          <YAxis 
            stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
            fontSize={11}
            axisLine={false}
            tickLine={false}
            domain={[0, yAxisMax]}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: isDarkMode ? '#1F2937' : 'white',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              fontSize: '13px'
            }}
            formatter={(value, name) => [value, `${name} Prioridad`]}
            labelFormatter={(label) => `${label}`}
          />

          {/* Área para prioridad Crítica - Rojo */} 
          {visibleSeries.Critica && (
            <Area 
              type="monotone" 
              dataKey="Critica" 
              stroke="#DC2626" 
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#criticaGradient)"
              name="Crítica"
            />
          )}
          
          {/* Área para prioridad Alta - Rojo */}
          {visibleSeries.Alta && (
            <Area 
              type="monotone" 
              dataKey="Alta" 
              stroke="#EA580C" 
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#altaGradient)"
              name="Alta"
            />
          )}
          
          {/* Área para prioridad Media - Amarillo */}
          {visibleSeries.Media && (
            <Area 
              type="monotone" 
              dataKey="Media" 
              stroke="#F59E0B"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#mediaGradient)"
              name="Media"
            />
          )}
          
          {/* Área para prioridad Baja - Azul */}
          {visibleSeries.Baja && (
            <Area 
              type="monotone" 
              dataKey="Baja" 
              stroke="#3B82F6"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#bajaGradient)"
              name="Baja"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
      
      {/* Leyenda personalizada interactiva */}
      <div className="flex justify-center mt-3 space-x-8">
        <button
          onClick={() => toggleSeries('Critica')}
          className={`flex items-center space-x-2 px-3 py-1 rounded-lg transition-all duration-200 ${
            visibleSeries.Critica 
              ? 'opacity-100 hover:bg-white/10' 
              : 'opacity-40 hover:opacity-60'
          }`}
          title={visibleSeries.Critica ? 'Click para ocultar' : 'Click para mostrar'}
        >
          <div className={`w-3 h-2 bg-red-600 rounded-sm ${visibleSeries.Critica ? 'opacity-80' : 'opacity-30'}`}></div>
          <span className="text-xs font-medium">Crítica</span>
        </button>
        
        <button
          onClick={() => toggleSeries('Alta')}
          className={`flex items-center space-x-2 px-3 py-1 rounded-lg transition-all duration-200 ${
            visibleSeries.Alta 
              ? 'opacity-100 hover:bg-white/10' 
              : 'opacity-40 hover:opacity-60'
          }`}
          title={visibleSeries.Alta ? 'Click para ocultar' : 'Click para mostrar'}
        >
          <div className={`w-3 h-2 bg-orange-500 rounded-sm ${visibleSeries.Alta ? 'opacity-80' : 'opacity-30'}`}></div>
          <span className="text-xs font-medium">Alta</span>
        </button>
        
        <button
          onClick={() => toggleSeries('Media')}
          className={`flex items-center space-x-2 px-3 py-1 rounded-lg transition-all duration-200 ${
            visibleSeries.Media 
              ? 'opacity-100 hover:bg-white/10' 
              : 'opacity-40 hover:opacity-60'
          }`}
          title={visibleSeries.Media ? 'Click para ocultar' : 'Click para mostrar'}
        >
          <div className={`w-3 h-2 bg-yellow-500 rounded-sm ${visibleSeries.Media ? 'opacity-80' : 'opacity-30'}`}></div>
          <span className="text-xs font-medium">Media</span>
        </button>
        
        <button
          onClick={() => toggleSeries('Baja')}
          className={`flex items-center space-x-2 px-3 py-1 rounded-lg transition-all duration-200 ${
            visibleSeries.Baja 
              ? 'opacity-100 hover:bg-white/10' 
              : 'opacity-40 hover:opacity-60'
          }`}
          title={visibleSeries.Baja ? 'Click para ocultar' : 'Click para mostrar'}
        >
          <div className={`w-3 h-2 bg-blue-500 rounded-sm ${visibleSeries.Baja ? 'opacity-80' : 'opacity-30'}`}></div>
          <span className="text-xs font-medium">Baja</span>
        </button>
      </div>
    </div>
  );
};

export default TicketsTrend;